import { computed, observable, action, runInAction } from "mobx";
import { getParameterByName } from "@utils/Browser";
import rootService from "../@data/service";

const {
	getLeadByToken,
	getQuestions,
	addLeadPicks,
	findOpenLead,
	updateQuoteByClientToken,
} = rootService;

class ProfileActionsPanel {
	@observable lead;
	@observable quickPickQuestions = [];
	@observable currentPanelState = "default";

	provider;
	midtail;

	constructor(rootStore, staticContext) {
		this.rootStore = rootStore;
		this.provider = staticContext.provider;
		this.midtail = staticContext.midtail;
	}

	get accountStore() {
		const { accountStore } = this.rootStore.stores;
		return accountStore;
	}

	get profileStore() {
		const { profileStore } = this.rootStore.stores;
		return profileStore;
	}

	get service() {
		return this.profileStore.service;
	}

	get zipcode() {
		return this.profileStore.zipcode;
	}

	get leadKey() {
		return getParameterByName("leadKey") || this.lead?.key;
	}

	get serviceKey() {
		return getParameterByName("service");
	}

	@computed
	get quote() {
		return this.lead?.quotes.find(
			(quote) => quote.providerMongoID === this.provider?._id
		);
	}

	get servicesForDropDown() {
		const { services } = this.provider;
		if (!this.midtail || !this.midtail.midtail) {
			//if (services.length === 0) return [this.service].filter(Boolean);
			return services;
		}
		const filteredServices = services.filter(
			(service) =>
				!!this.midtail?.affinities
					.filter(({ affinity }) => !affinity.midtail)
					.find(({ affinity }) => affinity.key === service.key)
		);
		if (filteredServices.length === 0)
			return (
				this.midtail?.affinities
					.filter(({ affinity }) => !affinity.midtail)
					.map(({ affinity }) => affinity) || []
			);
		return filteredServices;
	}

	get showReviewButton() {
		return this.quote.accepted || this.quote.type === "bid";
	}

	@action
	async init() {
		try {
			await this.setLead();
			if (!this.quote) return;

			const { record: quote } = await updateQuoteByClientToken({
				filter: { _id: this.quote._id },
				record: { profile_viewed: true },
			});

			runInAction(() => {
				this.lead.quotes = this.lead.quotes.map((q) =>
					q._id === this.quote._id ? quote : q
				);
			});
		} catch (err) {
			throw err;
		}
	}

	@action
	async reload() {
		try {
			await this.setLead();
			if (this.currentPanelState === "default")
				await this.setQuickPickQuestions();
			runInAction(() => {
				this.currentPanelState = this.getCurrentPanelState();
			});
		} catch (err) {
			throw err;
		}
	}

	@action
	async setQuickPickQuestions() {
		try {
			if (!this.service) return;

			const questions = await getQuestions(
				{
					filter: { quickPick: true, active: true, service: this.service._id },
					sort: { position: 1 },
					filterAnswers: {
						active: true,
						_operators: { type: { ne: "extra" } },
					},
					sortAnswers: { position: 1 },
				},
				{ batch: true }
			);

			runInAction(() => {
				this.quickPickQuestions = questions;
				this.currentPanelState = this.getCurrentPanelState();
			});
		} catch (err) {
			throw err;
		}
	}

	@action
	async setLead() {
		try {
			let lead;
			if (!this.leadKey) {
				if (
					(this.accountStore.type === "CLIENT" ||
						this.accountStore.type === "USER") &&
					!this.service.midtail &&
					this.zipcode
				) {
					lead = await findOpenLead({
						serviceKey: this.service.key,
						zipcode: this.zipcode,
						quotesFilter: {
							provider: this.provider?._id,
						},
					});
				} else {
					this.currentPanelState = this.getCurrentPanelState();
					return;
				}
			} else if (
				(this.accountStore.type === "CLIENT" ||
					this.accountStore.type === "USER") &&
				!lead
			) {
				lead = await getLeadByToken(
					{
						filter: {
							key: this.leadKey,
						},
						quotesFilter: {
							provider: this.provider?._id,
						},
					},
					{ batch: true }
				);
			}

			runInAction(() => {
				this.lead = lead;
				this.currentPanelState = this.getCurrentPanelState();
			});
		} catch (err) {
			throw err;
		}
	}

	async sendProject() {
		try {
			await addLeadPicks({
				leadKey: this.leadKey,
				providers: [this.provider._id],
			});
			await this.reload();
		} catch (err) {
			throw err;
		}
	}

	@action
	updateService(serviceId) {
		this.profileStore.service = this.provider.services.find(
			(service) => service._id === serviceId
		);
		if (!this.profileStore.service) {
			const services =
				this.midtail?.affinities
					.filter(({ affinity }) => !affinity.midtail)
					.map(({ affinity }) => affinity) || [];
			this.profileStore.service = services.find(
				(service) => service._id === serviceId
			);
		}
	}

	getCurrentPanelState() {
		if (this.lead && !this.quote) return "oneClickBlast";
		if (this.quote) {
			if (this.quote.declined) return "declined";
			if (this.quote.type === "direct") return "hasDirect";
			if (this.quote.type === "bid" && this.quote.last_client_message)
				return "hasDirect";
			if (this.quote.type === "bid") return "hasBid";
		}
		return "default";
	}
}

export default ProfileActionsPanel;
